import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Colors } from 'modules/theme';
import Loader from 'components/Loader';
import H5 from 'components/H5';
import { getXAxisInterval } from 'utils/flowVolumeChart';
import {
  CHART_TOOLTIP_CURSOR_LINE,
  CHART_TOOLTIP_WRAPPER_STYLE,
  MaxFlowPressureTooltip,
} from './chartTooltip';
import { ChartPanel } from './flowVolume.styles';

const CHART_AXIS = '#94a3b8';
const CHART_GRID = '#e3edf5';

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(248, 251, 255, 0.82);
  z-index: 10;
  border-radius: 12px;
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #e8f1f8;
  box-shadow: 0 4px 16px rgba(20, 152, 203, 0.1);
`;

const hasPressureSeries = dataChart =>
  dataChart.some(
    point => point.psi_0 !== undefined || point.psi_1 !== undefined || point.psi_2 !== undefined,
  );

const MaxFlowPressureChart = ({ id, dataChart, loading, pressureLoading }) => {
  const xAxisInterval = getXAxisInterval(dataChart.length);
  const showInitialLoading = loading && dataChart.length === 0;
  const chartData = showInitialLoading ? [] : dataChart;
  const showPressureLines = !pressureLoading && hasPressureSeries(chartData);

  return (
    <ChartPanel id={id}>
      {(showInitialLoading || (loading && pressureLoading)) && (
        <LoadingOverlay>
          <LoadingCard>
            <Loader size={30} />
            <H5 color={Colors.gray8} style={{ marginTop: 10, margin: 0 }}>
              Loading chart data...
            </H5>
          </LoadingCard>
        </LoadingOverlay>
      )}
      <ResponsiveContainer width="100%" height="100%" debounce={50}>
        <ComposedChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
          <XAxis
            type="category"
            dataKey="name"
            scale="point"
            axisLine={false}
            tickLine={false}
            stroke={CHART_AXIS}
            tick={{ fontSize: 11, fill: CHART_AXIS }}
            interval={xAxisInterval}
            tickMargin={10}
            padding={{ left: 16, right: 16 }}
          />

          <YAxis
            type="number"
            axisLine={false}
            tickLine={false}
            stroke={CHART_AXIS}
            tick={{ fontSize: 11, fill: CHART_AXIS }}
            width={48}
          />

          <Tooltip
            content={<MaxFlowPressureTooltip />}
            cursor={CHART_TOOLTIP_CURSOR_LINE}
            wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
          />

          <CartesianGrid stroke={CHART_GRID} strokeDasharray="4 6" vertical={false} />

          <Line
            dataKey="gal"
            type="monotone"
            stroke={Colors.lightBlue}
            strokeWidth={2.5}
            dot={false}
            connectNulls
            isAnimationActive={!loading}
            animationDuration={600}
          />

          {showPressureLines && (
            <>
              <Line
                dataKey="psi_0"
                type="monotone"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                connectNulls
                animationDuration={800}
              />
              <Line
                dataKey="psi_1"
                type="monotone"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
                connectNulls
                animationDuration={900}
              />
              <Line
                dataKey="psi_2"
                type="monotone"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                connectNulls
                animationDuration={1000}
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
};

MaxFlowPressureChart.propTypes = {
  dataChart: PropTypes.array,
  id: PropTypes.string,
  loading: PropTypes.bool,
  pressureLoading: PropTypes.bool,
};

MaxFlowPressureChart.defaultProps = {
  dataChart: [],
  id: 'maxFlowAndPressureChart',
  loading: false,
  pressureLoading: false,
};

export default MaxFlowPressureChart;
